// src/components/layout/Sidebar.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { db, auth } from '../../firebase';
import { ADMIN_EMAILS } from '../../config';
import { FEATURES } from '../../config/features';
import { collection, query, orderBy, limit, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { 
  LayoutGrid, Sparkles, AlertTriangle, PlusCircle, ShieldCheck, 
  ChevronLeft, ChevronRight, ChevronUp, ChevronDown, TrendingUp, Award, FileText, Flame,
  ShoppingCart, User
} from 'lucide-react';
import { LOGO_URL } from '../../utils/constants';


export const Sidebar = ({ karma, collapsed, setCollapsed, sightings, hasUnreadAlerts, markAlertsAsRead }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [topUsers, setTopUsers] = useState([]);
  const [siteName, setSiteName] = useState("Motoradar24");
  const [marketExpanded, setMarketExpanded] = useState(false);

  useEffect(() => {
    const loadSiteName = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, "settings", "site"));
        if (settingsDoc.exists() && settingsDoc.data().name) {
          setSiteName(settingsDoc.data().name);
        }
      } catch (error) {
        console.error("Błąd ładowania nazwy:", error);
      }
    };
    loadSiteName();
  }, []);
  
  useEffect(() => {
    const q = query(collection(db, "users"), orderBy("points", "desc"), limit(5));
    return onSnapshot(q, (snap) => setTopUsers(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, []);

  const isAdmin = auth.currentUser && ADMIN_EMAILS.includes(auth.currentUser.email);

  const counts = {
    all: sightings.filter(s => !s.hidden).length,
    exclusive: sightings.filter(s => s.isExclusive && !s.hidden).length,
    alert: sightings.filter(s => s.isAlert && !s.hidden).length
  };

  const handleAlertClick = () => {
    markAlertsAsRead();
    navigate('/alert');
  };

  return (
    <div className={`${collapsed ? 'w-16' : 'w-64'} hidden lg:flex flex-col h-screen sticky top-0 transition-all duration-300 z-50 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white shadow-2xl`}>
      {/* Logo Section */}
      <div className="p-4 flex items-center justify-between border-b border-white/10">
        {!collapsed ? (
          <>
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Motoradar24" className="h-8 w-8 object-contain" onError={(e) => e.target.style.display = 'none'} />
              <span className="font-black text-xl">{siteName}</span>
            </Link>
            <button onClick={() => setCollapsed(!collapsed)} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <ChevronLeft size={18}/>
            </button>
          </>
        ) : (
          <div className="w-full flex flex-col items-center gap-2">
            <Link to="/" className="mx-auto">
              <img src={LOGO_URL} alt="Logo" className="h-8 w-8 object-contain" onError={(e) => e.target.style.display = 'none'} />
            </Link>
            <button onClick={() => setCollapsed(!collapsed)} className="p-2 hover:bg-white/10 rounded-lg transition-colors w-full flex justify-center">
              <ChevronRight size={18}/>
            </button>
          </div>
        )}
      </div>

      <div className="flex-1 px-2 py-4 space-y-1 overflow-y-auto custom-scrollbar">
        {/* SPOTTING SECTION */}
        {!collapsed && (
          <div className="px-3 py-2">
            <p className="text-[10px] font-bold text-gray-400 uppercase">Spotting</p>
          </div>
        )}

        <Link 
          to="/" 
          className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg transition-all text-sm font-semibold ${
            location.pathname === '/' 
              ? 'bg-blue-600 text-white shadow-lg' 
              : 'text-gray-300 hover:bg-white/10'
          }`}
        >
          <div className="flex items-center gap-3">
            <LayoutGrid size={20}/>
            {!collapsed && <span>Baza</span>}
          </div>
          {!collapsed && <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">{counts.all}</span>}
        </Link>

        <Link 
          to="/exclusive" 
          className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg transition-all text-sm font-semibold ${
            location.pathname === '/exclusive' 
              ? 'bg-gradient-to-r from-amber-600 to-orange-600 text-white shadow-lg' 
              : 'text-gray-300 hover:bg-white/10'
          }`}
        >
          <div className="flex items-center gap-3">
            <Sparkles size={20}/>
            {!collapsed && <span>Exclusive Garage</span>}
          </div>
          {!collapsed && <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">{counts.exclusive}</span>}
        </Link>

        <button
          onClick={handleAlertClick}
          className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg transition-all text-sm font-semibold relative ${
            location.pathname === '/alert' 
              ? 'bg-red-600 text-white shadow-lg' 
              : 'text-gray-300 hover:bg-white/10'
          }`}
        >
          <div className="flex items-center gap-3">
            <AlertTriangle size={20}/>
            {!collapsed && <span>Alert Zone</span>}
            {hasUnreadAlerts && (
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse border-2 border-slate-900"></div>
            )}
          </div>
          {!collapsed && (
            <div className="flex items-center gap-1">
              <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">{counts.alert}</span>
              {hasUnreadAlerts && <Flame size={12} className="text-red-400 animate-pulse"/>}
            </div>
          )}
        </button>

        {/* DŻBAN ROKU - only if feature enabled */}
        {FEATURES.DZBAN_ROKU && (
          <Link
            to="/dzban-roku"
            className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg transition-all text-sm font-semibold ${
              location.pathname.startsWith('/dzban-roku')
                ? 'bg-gradient-to-r from-red-600 to-orange-600 text-white shadow-lg'
                : 'text-gray-300 hover:bg-white/10'
            }`}
          >
            <div className="flex items-center gap-3">
              <Flame size={20}/>
              {!collapsed && <span>Dzban Roku</span>}
            </div>
            {!collapsed && (
              <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded-full animate-pulse">
                HOT
              </span>
            )}
          </Link>
        )}

        <Link 
          to="/add" 
          className="flex items-center gap-3 px-3 py-3 rounded-lg transition-all text-sm font-bold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg mt-2"
        >
          <PlusCircle size={20}/>
          {!collapsed && <span>Dodaj Obserwację</span>}
        </Link>

        {/* MAPA LIVE - link do subdomeny mapy */}
        <div className="pt-4 mt-4 border-t border-white/10">
          <a
            href="https://mapa.motoradar24.pl"
            target="_blank"
            rel="noopener noreferrer"
            className="block relative rounded-lg overflow-hidden group transition-all hover:scale-[1.02]"
          >
            <div className="relative h-16 bg-gradient-to-br from-slate-950 via-red-950/40 to-slate-950 flex items-center px-3 gap-3 border border-white/10 group-hover:border-red-500/50">
              {/* Mini mapa SVG jako tlo */}
              <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full opacity-20" preserveAspectRatio="xMidYMid slice">
                <circle cx="55" cy="35" r="3" fill="#E8231C"/>
                <circle cx="30" cy="50" r="2" fill="#E8231C"/>
                <circle cx="70" cy="55" r="2.5" fill="#E8231C"/>
                <circle cx="45" cy="65" r="1.8" fill="#E8231C"/>
                <circle cx="60" cy="25" r="1.5" fill="#E8231C"/>
              </svg>
              <div className="relative z-10 flex items-center gap-2 flex-1">
                <span className="text-xl">🗺️</span>
                {!collapsed && (
                  <div className="leading-tight">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-bold text-white">Live Mapa</span>
                      <span className="flex items-center gap-1 text-[9px] font-bold bg-red-600 text-white px-1.5 py-0.5 rounded-full">
                        <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                        LIVE
                      </span>
                    </div>
                    <p className="text-[10px] text-gray-400">Chamstwo na mapie Polski</p>
                  </div>
                )}
              </div>
            </div>
          </a>
        </div>

        {/* MARKETPLACE SECTION - collapsible */}
        <div className="pt-4 mt-4 border-t border-white/10">
          <button
            onClick={() => setMarketExpanded(!marketExpanded)}
            className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg transition-all text-sm font-semibold ${
              location.pathname.startsWith('/market')
                ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg'
                : 'text-gray-300 hover:bg-white/10'
            }`}
          >
            <div className="flex items-center gap-3">
              <ShoppingCart size={20}/>
              {!collapsed && <span>Marketplace</span>}
            </div>
            {!collapsed && (
              marketExpanded ? <ChevronUp size={16}/> : <ChevronDown size={16}/>
            )}
          </button>

          {marketExpanded && !collapsed && (
            <div className="mt-1 space-y-1 pl-1">
              <Link
                to="/market"
                className="flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm font-semibold text-gray-300 hover:bg-white/10"
              >
                <LayoutGrid size={16}/>
                <span>Przeglądaj</span>
              </Link>

              <Link
                to="/market/sell"
                className="flex items-center gap-3 px-3 py-3 rounded-lg transition-all text-sm font-bold bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg"
              >
                <PlusCircle size={20}/>
                <span>SPRZEDAJ</span>
              </Link>

              <Link
                to="/market/my-listings"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm font-semibold ${
                  location.pathname === '/market/my-listings'
                    ? 'bg-emerald-600 text-white shadow-lg'
                    : 'text-gray-300 hover:bg-white/10'
                }`}
              >
                <User size={20}/>
                <span>Moje Ogłoszenia</span>
              </Link>
            </div>
          )}
        </div>
        
        {/* ADMIN */}
        {isAdmin && (
          <div className="pt-4 mt-4 border-t border-white/10">
            <Link 
              to="/admin" 
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm font-semibold ${
                location.pathname === '/admin' 
                  ? 'bg-red-600 text-white shadow-lg' 
                  : 'text-gray-300 hover:bg-white/10'
              }`}
            >
              <ShieldCheck size={20}/>
              {!collapsed && <span>Admin</span>}
            </Link>
          </div>
        )}

        {!collapsed && (
          <>
            <div className="pt-4 mt-4 border-t border-white/10">
              <p className="text-[10px] font-bold text-gray-400 uppercase px-3 mb-2">Ostatnie Spoty</p>
              <div className="space-y-1">
                {sightings.slice(0, 3).map(s => {
                  // Generate correct URL
                  const url = s.flair === 'poradnik' && s.seoSlug
                    ? `/poradnik/${s.seoSlug}`
                    : s.plateSlug
                      ? `/pojazd/${s.plateSlug}`
                      : `/pojazd/${s.id}`;
                  
                  return (
                    <Link 
                      key={s.id} 
                      to={url}
                      className="flex items-center gap-2 px-3 py-2 hover:bg-white/10 rounded-lg group transition-colors"
                    >
                      <img src={s.thumbUrl || s.imageUrl} className="w-8 h-8 rounded object-cover" alt="m" loading="lazy"/>
                      <span className="text-xs font-semibold text-gray-300 group-hover:text-blue-400 truncate">
                        {s.flair === 'poradnik' ? (s.seoTitle || 'Poradnik') : s.plate}
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>

            <div className="pt-4 mt-4 border-t border-white/10">
              <p className="text-[10px] font-bold text-gray-400 uppercase px-3 mb-2 flex items-center gap-1">
                <TrendingUp size={12}/> Top Spotterzy
              </p>
              <div className="space-y-2 px-3">
                {topUsers.map((u, i) => (
                  <div key={u.id} className="flex justify-between items-center text-xs">
                    <span className="font-semibold text-gray-400">#{i+1}</span>
                    <span className="font-semibold text-gray-300 truncate w-20">Spotter-{u.id.substring(0,4)}</span>
                    <span className="font-bold text-blue-400">{u.points}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Footer in Sidebar */}
      <div className="border-t border-white/10">
        {!collapsed && (
          <div className="p-4 space-y-3">
            <div className="flex items-center gap-3 px-3 py-2 bg-white/5 rounded-lg">
              <Award className="text-amber-500" size={18}/>
              <div className="leading-tight">
                <p className="text-[10px] font-bold text-gray-400">Twoje punkty</p>
                <p className="text-sm font-bold">{karma}</p>
              </div>
            </div>
            
            <div className="px-3 py-2 text-xs text-gray-400 space-y-2">
              <Link to="/regulamin" className="flex items-center gap-2 hover:text-blue-400 transition-colors">
                <FileText size={14}/>
                <span>Regulamin</span>
              </Link>
              {isAdmin && (
                <Link to="/admin" className="flex items-center gap-2 hover:text-red-400 transition-colors">
                  <ShieldCheck size={14}/>
                  <span>Panel Admina</span>
                </Link>
              )}
              <div className="text-gray-500 text-[10px] mt-2">
                © {new Date().getFullYear()} Motoradar24
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};